import React from 'react';
import type { CheckAdjudication } from '@privisy/api-types';
import { useOpenCrosscheckExplainer } from './ExplainerContext';

// Card-level AI Crosscheck pieces (#1659). Violet and gray tokens only, never
// green: green means passing, and a crosscheck reading on a failing card is
// not a pass.

const COVERAGE_FALLBACK = 'covers this requirement';

function quoted(text: string): string {
  return '“' + text + '”';
}

/** Host of the page the quote was verified against; null when absent or unparseable. */
export function quoteHost(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

export function DisputedChip() {
  return <span className="crosscheck-chip crosscheck-chip--disputed">Disputed</span>;
}

export function ConfirmedChip() {
  return <span className="crosscheck-chip crosscheck-chip--confirmed">✓ Confirmed</span>;
}

/** Right-aligned header link; rendered only where a provider can open the explainer. */
export function DisputedExplainLink() {
  const open = useOpenCrosscheckExplainer();
  if (!open) return null;
  return (
    <button type="button" className="crosscheck-explain-link no-print" onClick={open}>
      What does disputed mean?
    </button>
  );
}

/** A quiet labelled row: "AI Crosscheck agrees" + reasoning, or a correction note. */
export function CrosscheckRow({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div className="crosscheck-row">
      <span className="crosscheck-row__label">{label}</span>
      {children}
    </div>
  );
}

export function CrosscheckQuote({ quote }: { quote: string }) {
  return <blockquote className="crosscheck-quote">{quoted(quote)}</blockquote>;
}

interface TwoReadingsProps {
  adjudication: CheckAdjudication;
  /** Which side the pattern check came down on. */
  patternFound: boolean;
  /** The check's own finding sentence, shown under the pattern check's verdict. */
  patternText?: string;
}

/**
 * PATTERN CHECK beside AI CROSSCHECK, stacked below 640px. A failed pattern
 * check is answered by the verified quote; a passed one by the reasoning.
 */
export function TwoReadings({ adjudication, patternFound, patternText }: TwoReadingsProps) {
  const host = quoteHost(adjudication.quoteSourceUrl);
  const aiVerdict = patternFound ? "Doesn't satisfy it" : adjudication.quote ? 'Found, with a quote' : 'Found';

  return (
    <div className="crosscheck-readings">
      <div role="group" aria-label="Pattern check" className="crosscheck-reading">
        <span className="crosscheck-reading__heading" aria-hidden="true">Pattern check</span>
        <span className="crosscheck-reading__verdict">{patternFound ? 'Found' : 'Not found'}</span>
        {patternText && <p className="crosscheck-reading__text">{patternText}</p>}
      </div>
      <div role="group" aria-label="AI Crosscheck" className="crosscheck-reading crosscheck-reading--ai">
        <span className="crosscheck-reading__heading" aria-hidden="true">AI Crosscheck</span>
        <span className="crosscheck-reading__verdict">{aiVerdict}</span>
        {!patternFound && adjudication.quote && (
          <>
            <CrosscheckQuote quote={adjudication.quote} />
            <span className="crosscheck-reading__source">
              {host ? `${host} · quote checked word for word` : 'quote checked word for word'}
            </span>
          </>
        )}
        {patternFound && adjudication.reasoning && <p className="crosscheck-reading__text">{adjudication.reasoning}</p>}
      </div>
    </div>
  );
}

/** The "Your call" row's text under the readings on a disputed failing card. */
export function yourCallText(coverage?: string): string {
  return `Read the quoted passage. If it ${coverage || COVERAGE_FALLBACK}, this is likely covered. The score still counts it as an issue, because AI Crosscheck is advisory.`;
}
